<template>
  <div class="card-container">
    <div class="header-container">
      <div class="title">
        <div class="circle-symbol"></div>
        <div>{{ cardInfo.name }}</div>
        <el-icon class="add-btn" @click="onAdd">
          <Plus />
        </el-icon>
      </div>
      <el-checkbox v-model="cfg.isCheckAll" :indeterminate="cfg.isHalf" @change="handleGroupClicked"></el-checkbox>
    </div>
    <div class="main-container">
      <el-scrollbar max-height="200px" v-if="true">
        <el-checkbox-group class="select-group" v-model="cfg.checkedList">
          <div class="select-item" v-for="(item, index) in cfg.checkBoxList" :key="item">
            <!-- 选项的名称 -->
            <el-checkbox :label="item.key" @change="handleItemClicked($event, item.key)">
              <span class="item-name">{{ item.name }}</span>
            </el-checkbox>
            <!-- 操作btn区域 -->
            <div class="btn-container">
              <el-button type="text" :icon="Edit" @click="onEdit(item)" :disabled="isConfirming"></el-button>
              <el-button type="text" :icon="Delete" @click="onDelete(item)" :disabled="isConfirming"></el-button>
            </div>
          </div>
        </el-checkbox-group>
      </el-scrollbar>
    </div>

    <!-- 弹窗: 添加|编辑 需量曲线 -->
    <EditModal v-if="cfg.editCfg.isShow" v-model:show="cfg.editCfg.isShow" :fOptions="cfg.editCfg"
      @lineDataChange="handleLineDataChange" @closeModal="cfg.editCfg.isShow = false; cfg.editCfg.type = ''" />

  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, reactive, watchEffect } from 'vue'
import { Edit, Delete, Plus } from '@element-plus/icons-vue'
import EditModal from './editModal.vue'
import { queryDeleteDemandLine } from '../api/index.js';
import { ElMessageBox } from 'element-plus'

const $str = window.$str;

const props = defineProps({
  cardInfo: {
    type: Object,
    default: null,
    required: true,
  },
})

const emit = defineEmits(['selectedChange', 'lineDataChange']);
const isConfirming = ref(false);


// 控制Card的配置对象
const cfg = reactive({
  isHalf: false, // 控制组选框是否处于半选状态
  isCheckAll: false, // 控制组选框是否处于全选状态
  checkBoxList: [], // 选项列表
  checkedList: [], // 被选中项的key列表
  editCfg: {
    isShow: false,
    type: 'add', // 'add' | 'edit'
    lineId: '',
    groupType: -1, // -1-异常值 0-实际需量 1-储能需量 2-负荷需量
    addMeterList: [], // 【加项-电表UUID】 用于编辑时，回显
    subMeterList: [], // 【减项-电表UUID】 用于编辑时，回显
  },
})




/** ====== [业务：初始化方法] =================================================================== */
/**
 * 自适应初始化选项列表
 * >>> 监听父组件传递的列表项数据的变化
 * >>> 更新：卡片的列表项数据
 * >>> 更新：卡片的列表项-各项的选中状态
 */
watch(() => props.cardInfo.children, nv => {
  cfg.checkBoxList = nv ?? [];
  cfg.checkedList = getCheckedList(nv);
}, { immediate: true })

watch(() => props.cardInfo.groupKey, nv => {
  if (nv === 'actualDemand') {
    cfg.editCfg.groupType = 0;
  } else if (nv === 'energyDemand') {
    cfg.editCfg.groupType = 1;
  } else if (nv === 'loadDemand') {
    cfg.editCfg.groupType = 2;
  } else {
    cfg.editCfg.groupType = -1;
  }
}, { immediate: true })


/**
 * 自适应控制组选框的状态 >>> 通过监听相关数据的变化
 */
watchEffect(() => {
  const listCount = cfg.checkBoxList.length;
  const selectedCount = cfg.checkedList.length;

  if (listCount === 0) {
    cfg.isHalf = false;
    cfg.isCheckAll = false;
  } else {
    if (selectedCount === 0) {
      cfg.isHalf = false;
      cfg.isCheckAll = false;
    } else if (selectedCount === listCount) {
      cfg.isHalf = false;
      cfg.isCheckAll = true;
    } else {
      cfg.isHalf = true;
      cfg.isCheckAll = false;
    }

    // 更新组选项中的isShow的状态
    cfg.checkBoxList.forEach(line => {
      line.isShow = cfg.checkedList.some(it => it === line.key);
    })
  }
})



/** ====== [业务：功能方法] =================================================================== */
/**
 * 事件响应：组选框被点击
 * >>> 修改组成员选中状态
 * >>> 通知父组件，控制图表渲染更新
 * @param {*} selectState 选中状态
 */
function handleGroupClicked(selectState) {
  // 更新组选项中的isShow的状态
  cfg.checkedList = selectState ? cfg.checkBoxList.map(it => it.key) : [];
  cfg.checkBoxList.forEach(line => {
    line.isShow = cfg.checkedList.some(it => it === line.key);
  })

  // 通知父组件，勾选项发生变化
  const msg = {
    type: 'all', // single | all
    // groupId: key,
    check: selectState,
  }
  handleCheckedChange(msg);
}


/**
 * 事件响应：选项被点击
 * >>> 通知父组件，控制图表更新渲染该曲线
 * @param {*} selectState 选中状态
 * @param {*} key 选项的key
 */
function handleItemClicked(selectState, key) {
  // 更新组选项中的isShow的状态
  cfg.checkBoxList.forEach(line => {
    line.isShow = cfg.checkedList.some(it => it === line.key);
  })

  // 通知父组件，勾选项发生变化
  const msg = {
    type: 'single', // single | all
    id: key,
    check: selectState,
  }
  handleCheckedChange(msg);
}


/**
 * 事件响应：新增曲线
 * >>> 弹窗 >>> 新增保存 >>> 通知父组件更新分组数据
 * @param {*} item 
 */
function onAdd() {
  cfg.editCfg.type = 'add';
  cfg.editCfg.addMeterList = [];
  cfg.editCfg.subMeterList = [];
  cfg.editCfg.isShow = true;
}


/**
 * 事件响应：编辑曲线
 * >>> 弹窗 >>> 编辑保存 >>> 通知父组件更新分组数据
 * @param {*} item 
 */
function onEdit(item) {
  cfg.editCfg.type = 'edit';
  cfg.editCfg.isShow = true;
  // 传递：加项减项列表 >>> 用于回显
  cfg.editCfg.addMeterList = item.addMeterList ?? [];
  cfg.editCfg.subMeterList = item.subMeterList ?? [];
  // 传递-曲线名称 && ID
  cfg.editCfg.lineId = item.id;
  cfg.editCfg.lineName = item.name;

}

/**
 * 事件响应：删除曲线
 * >>> 提示 >>> 删除 >>> 通知父组件更新分组数据
 * @param {*} item 
 */
async function onDelete(item) {
  const confirmMessage = `${$str.getKey('数据分析/是否删除需量曲线')}【${item.name}】？`;
  const userAgree = await ElMessageBox.confirm(confirmMessage, $str.getKey('数据分析/系统提示'), {
    confirmButtonText: $str.getKey('数据分析/确定'),
    cancelButtonText: $str.getKey('数据分析/取消'),
    "show-close": false,
    confirmButtonClass: 'custom-confirm-btn',
    cancelButtonClass: 'custom-cancel-btn',
    type: "warning"
  }).then(res => {
    return true;
  }).catch(err => {
    return false
  });

  if (!userAgree) { return false; }

  isConfirming.value = true;
  queryDeleteDemandLine(item.id).then(res => {
    if (res?.code == 200) {
      window.dispatchNotify({
        type: 'success',
        message: $str.getKey('数据分析/删除成功')
      })
      const msg = {
        key: item.key,
        operateType: 'delete',
      }
      handleLineDataChange(msg);
    }
  }).catch(err => {
    console.error('需量曲线删除失败：', err)
    window.dispatchNotify({
      type: 'error',
      message: $str.getKey('数据分析/删除失败')
    })
  }).finally(() => {
    isConfirming.value = false;
  })


}



/**
 * 事件响应：处理需要渲染的【需量曲线】发生了变化
 * @param {*} msg 
 *      >>> operateType 操作类型 - add | edit | delete
 *      >>> key 被操作曲线的唯一标识 - uuid
 */
function handleLineDataChange(msg) {
  emit('lineDataChange', msg);
}



/**
 * 事件响应：需要渲染的曲线项【勾选】发生变化
 * @param {*} msg 
 *      >>> operateType 操作类型 - add | edit | delete
 *      >>> key 被操作曲线的唯一标识 - uuid
 */
function handleCheckedChange(msg) {
  emit('selectedChange', msg);
}







/** ====== [辅助：功能方法] =================================================================== */
/**
 * 获取【组选中项的列表】
 */
function getCheckedList(checkBoxList = []) {
  const keyList = [];
  checkBoxList.forEach(it => {
    if (it.isShow) {
      keyList.push(it.key);
    }
  });
  return keyList;
}








</script>

<style lang="scss" scoped>
.card-container {
  min-height: 100px;
  max-height: 300px;
  box-sizing: border-box;
  margin: 10px;
  padding: 10px;
  background: rgba(255, 255, 255, 1);
  border: 1px solid rgba(192, 207, 218, 0.3);
  border-radius: 12px;

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .title {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      font-family: "HarmonyOS Sans SC", sans-serif;
      color: rgba(46, 46, 46, 0.85);
      font-weight: 400;

      .circle-symbol {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: #3a68ff;
      }

      .add-btn {
        color: rgba(46, 46, 46, 0.5);
        cursor: pointer;
        transition: color 0.2s;

        &:hover {
          color: #3a68ff;
        }
      }
    }

    ::v-deep .el-checkbox {
      .el-checkbox__input {
        .el-checkbox__inner {
          background-color: #FFFFFF;
          border-color: rgba(192, 207, 218, 0.3);
          width: 16px;
          height: 16px;

          &:after {
            border-color: #3a68ff;
          }
        }

        &.is-checked {
          .el-checkbox__inner {
            background-color: #3a68ff;
            border-color: #3a68ff;

            &:after {
              border-color: #FFFFFF;
            }
          }
        }

        &.is-indeterminate {
          .el-checkbox__inner {
            background-color: #3a68ff;
            border-color: #3a68ff;

            &:before {
              background-color: #FFFFFF;
            }
          }
        }
      }

      .el-checkbox__label {
        color: rgba(46, 46, 46, 0.85);
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
      }
    }
  }

  .main-container {
    margin: 10px 0;
    width: 100%;
    max-height: 230px;

    .el-scrollbar {
      width: 100%;
      flex: 1;
      padding-right: 20px;
    }

    .select-group {
      display: flex;
      flex-direction: column;

      /** 设置为UI上的样式 */
      .select-item {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;

        ::v-deep .el-checkbox {
          width: calc(100% - 70px);
          min-height: 32px;
          height: auto;
          padding: 6px 10px;
          border-radius: 6px;
          margin: 2px 0;
          transition: all 0.2s;
          align-items: flex-start;

          &.is-checked {
            background: rgba(58, 104, 255, 0.1);
          }

          .el-checkbox__input.is-checked {
            .el-checkbox__inner {
              background-color: #3a68ff;
              border-color: #3a68ff;

              &::after {
                border-color: #FFFFFF;
              }
            }

            &+.el-checkbox__label {
              color: #3a68ff !important;
              font-family: "HarmonyOS Sans SC", sans-serif;
              font-size: 14px;
              font-weight: 500;
            }
          }

          .el-checkbox__input:not(.is-checked) {
            .el-checkbox__inner {
              background-color: #FFFFFF;
              border-color: rgba(192, 207, 218, 0.3);
            }

            &+.el-checkbox__label {
              color: rgba(46, 46, 46, 0.85) !important;
              font-family: "HarmonyOS Sans SC", sans-serif;
              font-size: 14px;
            }
          }

          .el-checkbox__input {
            margin-top: 1px;
          }

          .el-checkbox__label {
            min-width: 0;
            line-height: 18px;
            white-space: normal;

            .item-name {
              display: -webkit-box;
              overflow: hidden;
              white-space: normal;
              word-break: break-all;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
            }
          }
        }

        .btn-container {
          position: absolute;
          height: 100%;
          right: 10px;
          display: flex;
          align-items: center;
          gap: 10px;

          ::v-deep .el-icon {
            font-size: 16px;
            transition: color 0.2s;
            color: rgba(46, 46, 46, 0.65);

            &:hover {
              color: #3a68ff;
            }
          }
        }

        // 选中状态下，图标颜色变为蓝色
        &:has(.el-checkbox.is-checked) .btn-container {
          ::v-deep .el-icon {
            color: #3a68ff;

            &:hover {
              color: #5B8DF4;
            }
          }
        }
      }
    }
  }
}
</style>